/**
 * Getypeerde toegang tot `/cancellation-policies`.
 *
 * De staffels gaan als geheel over de lijn, ook bij het bijwerken. Dat is geen
 * gemak maar een eis: ze stuk voor stuk kunnen wijzigen laat een tussentoestand
 * bestaan waarin "tot 7 dagen 50%" er al staat en "tot 30 dagen 100%" nog niet, en
 * wie op dat moment ruim van tevoren annuleert krijgt de helft.
 */
import { call, isRecord, readCount, readFlag, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type PolicyFailure = Failure
export type PolicyResult<T> = Result<T>

export interface CancellationTier {
  readonly daysBefore: number
  readonly refundPermille: number
}

/** Eén percentage, of een reeks staffels. */
export const CANCELLATION_MODES = ['flat', 'staged'] as const
export type CancellationMode = (typeof CANCELLATION_MODES)[number]

export interface CancellationPolicy {
  readonly id: string
  readonly name: string
  readonly mode: CancellationMode
  readonly flatRefundPermille: number
  readonly graceHours: number
  readonly refundCleaningFee: boolean
  readonly tiers: readonly CancellationTier[]
  readonly residenceIds: readonly string[]
}

export interface PolicyBody {
  readonly name: string
  readonly mode: CancellationMode
  readonly flatRefundPermille: number
  readonly graceHours: number
  readonly refundCleaningFee: boolean
  readonly tiers: readonly CancellationTier[]
}

function readTier(value: unknown): CancellationTier | null {
  if (!isRecord(value)) {
    return null
  }
  const daysBefore = value['daysBefore']
  const refundPermille = value['refundPermille']
  if (!Number.isSafeInteger(daysBefore) || !Number.isSafeInteger(refundPermille)) {
    return null
  }
  return { daysBefore: daysBefore as number, refundPermille: refundPermille as number }
}

function readPolicy(value: unknown): CancellationPolicy | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  if (id === '') {
    return null
  }

  const tiers: CancellationTier[] = []
  if (Array.isArray(value['tiers'])) {
    for (const entry of value['tiers']) {
      const tier = readTier(entry)
      if (tier !== null) {
        tiers.push(tier)
      }
    }
  }

  const residenceIds: string[] = []
  if (Array.isArray(value['residenceIds'])) {
    for (const entry of value['residenceIds']) {
      if (typeof entry === 'string' && entry !== '') {
        residenceIds.push(entry)
      }
    }
  }

  const mode = value['mode']
  return {
    id,
    tiers,
    residenceIds,
    name: readText(value, 'name'),
    // Onbekend leest als `staged`: dat is wat een voorwaarde met staffels is, en
    // hem als `flat` lezen zou een lijst staffels doen verdwijnen van het scherm.
    mode: mode === 'flat' ? 'flat' : 'staged',
    flatRefundPermille: readCount(value, 'flatRefundPermille'),
    graceHours: readCount(value, 'graceHours'),
    refundCleaningFee: readFlag(value, 'refundCleaningFee'),
  }
}

export async function listPolicies(
  options: RequestOptions = {},
): Promise<PolicyResult<readonly CancellationPolicy[]>> {
  return call(
    'GET',
    '/cancellation-policies',
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['policies'])) {
        return null
      }
      const policies: CancellationPolicy[] = []
      for (const entry of payload['policies']) {
        const policy = readPolicy(entry)
        if (policy !== null) {
          policies.push(policy)
        }
      }
      return policies
    },
    options,
  )
}

function readOne(payload: unknown): CancellationPolicy | null {
  return isRecord(payload) ? readPolicy(payload['policy']) : null
}

export async function createPolicy(body: PolicyBody): Promise<PolicyResult<CancellationPolicy>> {
  return call('POST', '/cancellation-policies', body, readOne)
}

export async function savePolicy(
  id: string,
  body: PolicyBody,
): Promise<PolicyResult<CancellationPolicy>> {
  return call('PUT', `/cancellation-policies/${encodeURIComponent(id)}`, body, readOne)
}

export async function deletePolicy(id: string): Promise<PolicyResult<true>> {
  return call('DELETE', `/cancellation-policies/${encodeURIComponent(id)}`, undefined, () => true)
}

/**
 * Voorzetten die de meeste verhuurders herkennen.
 *
 * Ze staan hier en niet in de database omdat het geen gegevens zijn maar een
 * startpunt: zodra iemand er een kiest, wordt het zíjn voorwaarde en mag hij er
 * alles aan veranderen. Een tabel met "standaardvoorwaarden" zou suggereren dat er
 * iets aan vastzit.
 */
export const POLICY_PRESETS: Readonly<Record<string, PolicyBody>> = {
  flexible: {
    name: 'Flexibel',
    mode: 'staged',
    flatRefundPermille: 0,
    graceHours: 24,
    refundCleaningFee: true,
    tiers: [{ daysBefore: 1, refundPermille: 1000 }],
  },
  moderate: {
    name: 'Gematigd',
    mode: 'staged',
    flatRefundPermille: 0,
    graceHours: 24,
    refundCleaningFee: true,
    tiers: [
      { daysBefore: 30, refundPermille: 1000 },
      { daysBefore: 7, refundPermille: 500 },
    ],
  },
  strict: {
    name: 'Streng',
    mode: 'staged',
    flatRefundPermille: 0,
    graceHours: 24,
    refundCleaningFee: true,
    tiers: [
      { daysBefore: 60, refundPermille: 1000 },
      { daysBefore: 30, refundPermille: 500 },
    ],
  },
  half: {
    name: 'Altijd de helft terug',
    mode: 'flat',
    flatRefundPermille: 500,
    graceHours: 24,
    refundCleaningFee: true,
    tiers: [],
  },
  nonRefundable: {
    name: 'Niet-restitueerbaar',
    mode: 'flat',
    flatRefundPermille: 0,
    graceHours: 0,
    refundCleaningFee: false,
    tiers: [],
  },
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function policyErrorMessageKey(failure: PolicyFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.cancellation.error.rejected'
    case 'not_found':
      return 'dashboard.cancellation.error.notFound'
    case 'forbidden':
      return 'dashboard.cancellation.error.forbidden'
    case 'conflict':
      return 'dashboard.cancellation.error.inUse'
    default:
      return 'dashboard.cancellation.error.unavailable'
  }
}
